import React from 'react';

import { fetchTrackingLinks, type TrackingLink } from '../../tracking-links-api';

export interface LandingLinksRead {
  /** `loading`: the first read has not answered; `error`: it failed (a later failure keeps the last good list). */
  status: 'loading' | 'ready' | 'error';
  /** Every link landing on the page, its own link included. */
  links: TrackingLink[];
  /** A read is in flight (the first one, or a reload). */
  reading: boolean;
  reload: () => void;
}

/**
 * The tracking links landing on one Studio landing page (`GET …/tracking-links?landing_page_id=<id>`), its own link
 * included. Read on open and again on `reload()` (after a publish, an unpublish, a link moved here, or before an
 * unpublish is confirmed). Only the newest read lands; a re-read that fails keeps the last good list.
 */
export const useLandingLinks = (tenantId: number, pageId: string): LandingLinksRead => {
  const [tick, setTick] = React.useState(0);
  const [state, setState] = React.useState<Omit<LandingLinksRead, 'reload'>>({
    status: 'loading',
    links: [],
    reading: true,
  });
  const reqIdRef = React.useRef(0);

  // Syncs with spark-creatives: the links landing on this page.
  React.useEffect(() => {
    const reqId = ++reqIdRef.current;
    const controller = new AbortController();
    setState((cur) => (cur.reading ? cur : { ...cur, reading: true }));
    void fetchTrackingLinks(tenantId, { landingPageId: pageId }, controller.signal).then((res) => {
      if (reqId !== reqIdRef.current || controller.signal.aborted) return;
      if (res.ok) {
        setState({ status: 'ready', links: res.data, reading: false });
        return;
      }
      setState((cur) =>
        cur.status === 'ready' ? { ...cur, reading: false } : { status: 'error', links: [], reading: false },
      );
    });
    return () => controller.abort();
  }, [tenantId, pageId, tick]);

  const reload = React.useCallback(() => setTick((n) => n + 1), []);
  return { ...state, reload };
};

export default useLandingLinks;
